import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { AppContext } from '../../context/AppContext';
import { db } from '../../firebase/client';
import { collection, query, where, onSnapshot, doc, updateDoc, Timestamp, addDoc, getDocs, orderBy } from 'firebase/firestore';
import { Reservation, Restaurant } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Users, Clock, CheckCircle, XCircle, Plus, ChevronRight, AlertTriangle, Phone, MessageSquare, Edit2 } from 'lucide-react';
import { formatDateReadable, formatDateOnly } from '../../utils/dateUtils';
import NotificationService from '../../services/NotificationService';

const timeToMinutes = (time: string) => {
    if (!time) return 0;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
};

const AdminReservationsScreen: React.FC = () => {
    const { currentUser } = useAuth();
    const { getRestaurantById } = useData();
    const { view } = useContext(AppContext);
    
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'pending' | 'confirmed' | 'past'>('pending');
    const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('en-CA'));
    const [showTimelineModal, setShowTimelineModal] = useState(false);
    const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    
    const restaurant = useMemo(() => {
        return getRestaurantById(currentUser?.restaurantId || '');
    }, [currentUser?.restaurantId, getRestaurantById]);

    // Fetch Reservations
    useEffect(() => {
        if (!restaurant?.id) return;

        const q = query(
            collection(db, 'reservations'),
            where('restaurantId', '==', restaurant.id)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const resData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Reservation[];

            // Sort manually to avoid composite index requirement
            resData.sort((a: any, b: any) => {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 
                             (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 
                             (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
                return timeB - timeA;
            });

            setReservations(resData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching reservations:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [restaurant?.id]);

    const filteredReservations = useMemo(() => {
        const selectedStr = selectedDate;

        return reservations.filter(res => {
            const resDate = res.date?.toDate ? res.date.toDate() : new Date(res.date);
            const resDateStr = resDate.toLocaleDateString('en-CA'); // YYYY-MM-DD
            
            if (activeTab === 'pending') return res.status === 'pending';
            if (activeTab === 'confirmed') return res.status === 'confirmed' && resDateStr === selectedStr;
            if (activeTab === 'past') return res.status === 'declined' || (res.status === 'confirmed' && resDateStr < selectedStr);
            return false;
        });
    }, [reservations, activeTab, selectedDate]);

    // Capacity Logic for Selected Date (Peak Occupancy)
    const capacityStats = useMemo(() => {
        if (!restaurant?.reservationSettings) return { totalGuests: 0, peakGuests: 0, currentGuests: 0, percentage: 0 };
        
        const selectedStr = selectedDate;
        
        // Count confirmed guests for selected date
        const confirmedOnDay = reservations.filter(res => {
            if (res.status !== 'confirmed') return false;
            const resDate = res.date?.toDate ? res.date.toDate() : new Date(res.date);
            const resDateStr = resDate.toLocaleDateString('en-CA');
            return resDateStr === selectedStr;
        });

        // Calculate occupancy timeline
        const occupancyMap: { [min: number]: number } = {};
        const slotDuration = restaurant.reservationSettings.slotDurationMinutes || 90;

        confirmedOnDay.forEach(res => {
            const startMin = timeToMinutes(res.time);
            const endMin = startMin + slotDuration;
            for (let m = startMin; m < endMin; m++) {
                occupancyMap[m] = (occupancyMap[m] || 0) + res.guests;
            }
        });

        const peakGuests = Object.values(occupancyMap).length > 0 ? Math.max(...Object.values(occupancyMap)) : 0;
        const maxCapacity = restaurant.reservationSettings.maxCapacityPerSlot || 1;

        // Determine which stats to highlight
        const isToday = selectedStr === new Date().toLocaleDateString('en-CA');
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();
        const currentGuests = occupancyMap[nowMin] || 0;

        const displayGuests = isToday ? currentGuests : peakGuests;
        const percentage = Math.min(100, (displayGuests / maxCapacity) * 100);
        const peakPercentage = Math.min(100, (peakGuests / maxCapacity) * 100);

        return { 
            totalGuests: confirmedOnDay.length, 
            totalGuestsCount: confirmedOnDay.reduce((s, r) => s + r.guests, 0), 
            peakGuests, 
            currentGuests, 
            percentage, 
            peakPercentage,
            isToday 
        };
    }, [reservations, restaurant, selectedDate]);

    // Timeline Data for Detailed View
    const timelineData = useMemo(() => {
        if (!restaurant?.reservationSettings) return [];
        
        const selectedStr = selectedDate;
        const confirmedOnDay = reservations.filter(res => {
            if (res.status !== 'confirmed') return false;
            const resDate = res.date?.toDate ? res.date.toDate() : new Date(res.date);
            return resDate.toLocaleDateString('en-CA') === selectedStr;
        });

        const slots = [];
        const slotDuration = restaurant.reservationSettings.slotDurationMinutes || 90;
        const maxCapacity = restaurant.reservationSettings.maxCapacityPerSlot || 1;

        // Collect all unique reservation times + hours
        const hourCheckpoints = Array.from({ length: 16 }, (_, i) => (i + 8) * 60); // 08:00 to 23:00
        
        hourCheckpoints.forEach(currentMin => {
            const guestsInSlot = confirmedOnDay.filter(res => {
                const startMin = timeToMinutes(res.time);
                const endMin = startMin + slotDuration;
                return currentMin >= startMin && currentMin < endMin;
            }).reduce((sum, res) => sum + res.guests, 0);

            if (guestsInSlot > 0) {
                const h = Math.floor(currentMin / 60);
                const m = currentMin % 60;
                slots.push({
                    time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
                    guests: guestsInSlot,
                    resCount: confirmedOnDay.filter(res => {
                        const startMin = timeToMinutes(res.time);
                        const endMin = startMin + slotDuration;
                        return currentMin >= startMin && currentMin < endMin;
                    }).length
                });
            }
        });

        return slots;
    }, [reservations, restaurant, selectedDate]);

    const handleUpdateStatus = async (id: string, status: 'confirmed' | 'declined') => {
        try {
            await updateDoc(doc(db, 'reservations', id), { status });
            
            // Send Notification to Client
            const reservation = reservations.find(r => r.id === id);
            if (reservation && reservation.clientId && !reservation.clientId.startsWith('manual_')) {
                NotificationService.sendToUser(reservation.clientId, {
                    title: status === 'confirmed' ? "✅ Réservation confirmée !" : "❌ Réservation refusée",
                    body: status === 'confirmed' 
                        ? `Votre table chez ${restaurant?.name} est confirmée pour le ${formatDateReadable(reservation.date)}.`
                        : `Désolé, votre réservation chez ${restaurant?.name} a été refusée.`,
                    data: {
                        type: 'reservation_status',
                        reservationId: reservation.id,
                        restaurantId: restaurant?.id
                    }
                }).catch(err => console.error("Error sending status notification:", err));
            }
        } catch (error) {
            console.error("Error updating reservation status:", error);
        }
    };

    const handleEditClick = (res: Reservation) => {
        setEditingReservation(res);
        const resDate = res.date?.toDate ? res.date.toDate() : new Date(res.date);
        setManualForm({
            clientName: res.clientName || '',
            clientPhone: res.clientPhone || '',
            guests: res.guests || 2,
            date: resDate.toLocaleDateString('en-CA'),
            time: res.time || '19:30',
            specialRequests: res.specialRequests || ''
        });
        setShowEditModal(true);
    };

    const handleUpdateReservation = async () => {
        if (!editingReservation || !manualForm.clientName) return;
        setIsSaving(true);
        try {
            const updatedData = {
                clientName: manualForm.clientName,
                clientPhone: manualForm.clientPhone,
                guests: manualForm.guests,
                date: Timestamp.fromDate(new Date(manualForm.date)),
                time: manualForm.time,
                specialRequests: manualForm.specialRequests,
            };

            await updateDoc(doc(db, 'reservations', editingReservation.id), updatedData);
            
            // Notify user about the change
            if (editingReservation.clientId && !editingReservation.clientId.startsWith('manual_')) {
                NotificationService.sendToUser(editingReservation.clientId, {
                    title: "📅 Réservation modifiée",
                    body: `Votre réservation chez ${restaurant?.name} a été mise à jour : ${manualForm.date} à ${manualForm.time}.`,
                    data: {
                        type: 'reservation_update',
                        reservationId: editingReservation.id
                    }
                }).catch(err => console.error("Error sending update notification:", err));
            }

            setShowEditModal(false);
            setEditingReservation(null);
        } catch (error) {
            console.error("Error updating reservation:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const [showManualModal, setShowManualModal] = useState(false);
    const [manualForm, setManualForm] = useState({
        clientName: '',
        clientPhone: '',
        guests: 2,
        date: new Date().toISOString().split('T')[0],
        time: '19:30',
        specialRequests: ''
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleAddManual = async () => {
        if (!manualForm.clientName || !manualForm.clientPhone) return;
        setIsSaving(true);
        try {
            await addDoc(collection(db, 'reservations'), {
                restaurantId: restaurant?.id,
                restaurantName: restaurant?.name,
                clientId: 'manual_' + Date.now(),
                clientName: manualForm.clientName,
                clientPhone: manualForm.clientPhone,
                guests: manualForm.guests,
                date: Timestamp.fromDate(new Date(manualForm.date)),
                time: manualForm.time,
                status: 'confirmed', // Manual entries are auto-confirmed
                specialRequests: manualForm.specialRequests,
                createdAt: Timestamp.now(),
                isManual: true
            });
            setShowManualModal(false);
            setManualForm({
                clientName: '',
                clientPhone: '',
                guests: 2,
                date: new Date().toISOString().split('T')[0],
                time: '19:30',
                specialRequests: ''
            });
        } catch (error) {
            console.error("Error adding manual reservation:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const availabilityStatus = useMemo(() => {
        if (!manualForm.date || !manualForm.time || !restaurant) return null;
        
        const slotStart = timeToMinutes(manualForm.time);
        const slotDuration = restaurant.reservationSettings?.slotDurationMinutes || 90;
        const maxCap = restaurant.reservationSettings?.maxCapacityPerSlot || 40;
        const proposedGuests = Number(manualForm.guests) || 0;
        
        // Find all confirmed reservations for the target date, excluding the one we're editing
        const dayReservations = reservations.filter(r => {
            if (r.status !== 'confirmed' || r.id === editingReservation?.id) return false;
            
            let rDateStr = '';
            if (r.date?.toDate) {
                const d = r.date.toDate();
                rDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            } else if (typeof r.date === 'string') {
                rDateStr = r.date.split('T')[0];
            } else {
                const d = new Date(r.date);
                rDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }
            return rDateStr === manualForm.date;
        });

        // Calculate peak occupancy within the proposed time slot
        let maxLoadInProposedWindow = 0;
        for (let t = slotStart; t < slotStart + slotDuration; t++) {
            let currentMinuteLoad = 0;
            dayReservations.forEach(res => {
                const resStart = timeToMinutes(res.time);
                const resEnd = resStart + slotDuration;
                if (t >= resStart && t < resEnd) {
                    currentMinuteLoad += Number(res.guests) || 0;
                }
            });
            if (currentMinuteLoad > maxLoadInProposedWindow) {
                maxLoadInProposedWindow = currentMinuteLoad;
            }
        }
        
        const totalWithProposed = maxLoadInProposedWindow + proposedGuests;
        const percentage = (totalWithProposed / maxCap) * 100;

        return {
            currentLoad: maxLoadInProposedWindow,
            total: totalWithProposed,
            max: maxCap,
            percentage,
            isFull: totalWithProposed > maxCap,
            isTight: percentage > 80
        };
    }, [manualForm, reservations, restaurant, editingReservation]);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white px-6 pt-8 pb-4 shadow-sm flex justify-between items-center border-b border-gray-50">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter">Réservations</h1>
                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
                        {formatDateOnly(selectedDate)}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => setShowTimelineModal(true)}
                        className="w-10 h-10 bg-gray-50 text-gray-900 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-sm"
                    >
                        <Clock size={18} />
                    </button>
                    <div className="relative">
                        <label className="w-10 h-10 bg-gray-900 text-white rounded-full flex items-center justify-center shadow-lg shadow-gray-200 cursor-pointer active:scale-95 transition-transform">
                            <Calendar size={18} />
                            <input 
                                type="date" 
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                            />
                        </label>
                    </div>
                </div>
            </div>

            {/* Compact Capacity Section */}
            {restaurant?.tableReservationEnabled && (
                <div className="px-6 mt-4">
                    <div className="grid grid-cols-2 gap-3">
                        {/* Occupation Box */}
                        <div className="bg-white p-3 py-4 rounded-[2rem] shadow-xl shadow-gray-200/40 border border-gray-100 flex flex-col items-center text-center">
                            <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">Occupation</span>
                            <div className="flex items-baseline gap-1">
                                <span className={`text-2xl font-black italic ${capacityStats.percentage > 90 ? 'text-red-600' : 'text-gray-900'}`}>
                                    {Math.round(capacityStats.percentage)}%
                                </span>
                            </div>
                            <div className="mt-1 flex flex-col">
                                <span className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">
                                    {capacityStats.isToday ? `Maintenant: ${capacityStats.currentGuests} pers` : `Pic prévu: ${capacityStats.peakGuests} pers`}
                                </span>
                                {capacityStats.isToday && capacityStats.peakPercentage > capacityStats.percentage && (
                                    <span className="text-[8px] font-black text-red-500 uppercase tracking-tighter">
                                        Rush prévu à {Math.round(capacityStats.peakPercentage)}%
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Reservations Box */}
                        <div className="bg-white p-3 py-4 rounded-[2rem] shadow-xl shadow-gray-200/40 border border-gray-100 flex flex-col items-center text-center">
                            <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">Confirmées</span>
                            <div className="flex items-baseline gap-1">
                                <span className="text-2xl font-black text-gray-900 italic">{capacityStats.totalGuests}</span>
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">Résas</span>
                            </div>
                            <span className="mt-0.5 text-[7px] font-bold text-gray-400 uppercase tracking-tighter">
                                Total: {capacityStats.totalGuestsCount} pers
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex px-6 mt-8 gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {(['pending', 'confirmed', 'past'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-6 py-3 rounded-full font-black text-[10px] uppercase tracking-widest transition-all whitespace-nowrap ${
                            activeTab === tab 
                            ? 'bg-gray-900 text-white shadow-lg shadow-gray-200' 
                            : 'bg-white text-gray-400 border border-gray-100'
                        }`}
                    >
                        {tab === 'pending' ? 'À Valider' : tab === 'confirmed' ? 'Confirmées' : 'Historique'}
                    </button>
                ))}
            </div>

            {/* List */}
            <div className="px-6 mt-4 space-y-4 flex-1">
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
                    </div>
                ) : filteredReservations.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                            <Calendar size={32} />
                        </div>
                        <p className="text-sm font-bold text-gray-400 uppercase">Aucune réservation ici</p>
                    </div>
                ) : (
                    filteredReservations.map((res) => (
                        <motion.div
                            layout
                            key={res.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white p-5 rounded-[2rem] shadow-sm border border-gray-100 group hover:shadow-md transition-all"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-900 group-hover:bg-gray-900 group-hover:text-white transition-colors shadow-inner">
                                        <Users size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-black text-gray-900 uppercase italic leading-tight">{res.clientName}</h3>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <Users size={12} className="text-gray-400" />
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{res.guests} personnes</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="flex items-center gap-1.5 justify-end text-red-600 font-black text-[10px] uppercase tracking-tighter italic">
                                        <Clock size={12} />
                                        <span>{res.time}</span>
                                    </div>
                                    <span className="text-[10px] font-bold text-gray-400 block mt-0.5">{formatDateOnly(res.date)}</span>
                                </div>
                            </div>

                            {res.specialRequests && (
                                <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                    <div className="flex items-center gap-2 mb-1">
                                        <MessageSquare size={12} className="text-gray-400" />
                                        <span className="text-[9px] font-black text-gray-400 uppercase">Notes :</span>
                                    </div>
                                    <p className="text-xs font-bold text-gray-600 italic">"{res.specialRequests}"</p>
                                </div>
                            )}

                            <div className="flex items-center gap-2">
                                <a 
                                    href={`tel:${res.clientPhone}`}
                                    className="flex-1 py-3 bg-gray-50 text-gray-900 rounded-xl flex items-center justify-center gap-2 border border-gray-100 hover:bg-gray-100 transition-colors"
                                >
                                    <Phone size={14} />
                                    <span className="text-[10px] font-black uppercase tracking-widest">{res.clientPhone}</span>
                                </a>
                                {res.status === 'confirmed' && (
                                    <button 
                                        onClick={() => handleEditClick(res)}
                                        className="w-12 h-12 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center hover:bg-gray-100 transition-colors border border-gray-100"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                )}
                                
                                {res.status === 'pending' && (
                                    <>
                                        <button 
                                            onClick={() => handleUpdateStatus(res.id, 'declined')}
                                            className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors border border-red-100"
                                        >
                                            <XCircle size={20} />
                                        </button>
                                        <button 
                                            onClick={() => handleUpdateStatus(res.id, 'confirmed')}
                                            className="w-12 h-12 rounded-xl bg-green-600 text-white flex items-center justify-center shadow-lg shadow-green-100 hover:bg-green-700 transition-colors"
                                        >
                                            <CheckCircle size={20} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    ))
                )}
            </div>

            {/* FAB for manual entry */}
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowManualModal(true)}
                className="fixed bottom-24 right-6 w-14 h-14 bg-gray-900 text-white rounded-2xl shadow-2xl flex items-center justify-center z-40 border-2 border-white/10"
            >
                <Plus size={24} />
            </motion.button>

            {/* Entry Modals */}
            <AnimatePresence>
                {(showManualModal || showEditModal) && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => { setShowManualModal(false); setShowEditModal(false); setEditingReservation(null); }}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                        />
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
                        >
                            <div className="p-8">
                                <h3 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter mb-6">
                                    {showEditModal ? 'Modifier Réservation' : 'Nouvelle Réservation'}
                                </h3>
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">Nom du Client</label>
                                        <input 
                                            type="text"
                                            value={manualForm.clientName}
                                            onChange={(e) => setManualForm({...manualForm, clientName: e.target.value})}
                                            className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none focus:border-gray-900 transition-all"
                                            placeholder="Ex: Jean Dupont"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">Téléphone</label>
                                            <input 
                                                type="tel"
                                                value={manualForm.clientPhone}
                                                onChange={(e) => setManualForm({...manualForm, clientPhone: e.target.value})}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none focus:border-gray-900 transition-all"
                                                placeholder="0X XX XX XX XX"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">Couverts</label>
                                            <input 
                                                type="number"
                                                value={manualForm.guests}
                                                onChange={(e) => setManualForm({...manualForm, guests: parseInt(e.target.value)})}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none focus:border-gray-900 transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">Date</label>
                                            <input 
                                                type="date"
                                                value={manualForm.date}
                                                onChange={(e) => setManualForm({...manualForm, date: e.target.value})}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none focus:border-gray-900 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">Heure</label>
                                            <input 
                                                type="time"
                                                value={manualForm.time}
                                                onChange={(e) => setManualForm({...manualForm, time: e.target.value})}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none focus:border-gray-900 transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">Notes spéciales</label>
                                        <textarea 
                                            value={manualForm.specialRequests}
                                            onChange={(e) => setManualForm({...manualForm, specialRequests: e.target.value})}
                                            className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-4 py-4 text-sm font-bold outline-none focus:border-gray-900 transition-all h-20 resize-none"
                                            placeholder="Allergies, anniversaire, etc."
                                        />
                                    </div>

                                    {/* Real-time Capacity Badge */}
                                    {availabilityStatus && (
                                        <div className={`p-4 rounded-2xl border flex items-center justify-between ${
                                            availabilityStatus.isFull ? 'bg-red-50 border-red-100 text-red-600' : 
                                            availabilityStatus.isTight ? 'bg-orange-50 border-orange-100 text-orange-600' : 
                                            'bg-green-50 border-green-100 text-green-600'
                                        }`}>
                                            <div className="flex items-center gap-2">
                                                {availabilityStatus.isFull ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black uppercase tracking-widest leading-none">
                                                        {availabilityStatus.isFull ? 'Salle Complète' : 
                                                         availabilityStatus.isTight ? 'Presque Complet' : 'Disponible'}
                                                    </span>
                                                    <span className="text-[8px] font-bold opacity-70 uppercase mt-0.5">
                                                        {availabilityStatus.currentLoad} déjà réservés + {manualForm.guests} pers
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-sm font-black italic block leading-none">
                                                    {availabilityStatus.total}
                                                </span>
                                                <span className="text-[8px] font-bold opacity-60 uppercase">/ {availabilityStatus.max}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-8 flex gap-3">
                                    <button 
                                        onClick={() => { setShowManualModal(false); setShowEditModal(false); setEditingReservation(null); }}
                                        className="flex-1 py-4 bg-gray-50 text-gray-400 rounded-2xl font-black uppercase tracking-widest text-[10px] active:scale-95 transition-transform"
                                    >
                                        Annuler
                                    </button>
                                    <button 
                                        onClick={showEditModal ? handleUpdateReservation : handleAddManual}
                                        disabled={isSaving}
                                        className="flex-1 py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-gray-200 active:scale-95 transition-transform flex items-center justify-center gap-2"
                                    >
                                        {isSaving ? (
                                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                <CheckCircle size={14} />
                                                {showEditModal ? 'Mettre à jour' : 'Confirmer'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showTimelineModal && (
                    <div className="fixed inset-0 z-[100] flex items-end justify-center">
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowTimelineModal(false)}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                        />
                        <motion.div 
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            className="relative bg-white w-full max-w-lg rounded-t-[3rem] p-8 pb-12 shadow-2xl overflow-y-auto max-h-[80vh]"
                        >
                            <div className="flex justify-between items-center mb-8">
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900 italic uppercase">Planning Horaire</h2>
                                    <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{formatDateOnly(selectedDate)}</p>
                                </div>
                                <button 
                                    onClick={() => setShowTimelineModal(false)}
                                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500"
                                >
                                    <XCircle size={20} />
                                </button>
                            </div>

                            {timelineData.length === 0 ? (
                                <div className="text-center py-12">
                                    <Clock size={40} className="text-gray-200 mx-auto mb-4" />
                                    <p className="text-gray-400 font-bold uppercase text-xs">Aucune occupation pour ce jour</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {timelineData.map((slot, index) => (
                                        <div key={index} className="flex items-center gap-6 p-4 bg-gray-50 rounded-3xl border border-gray-100">
                                            <div className="w-16">
                                                <span className="text-lg font-black text-gray-900 italic">{slot.time}</span>
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase">{slot.resCount} réservations</span>
                                                    <span className="text-xs font-black text-gray-900">{slot.guests} / {restaurant.reservationSettings?.maxCapacityPerSlot} pers</span>
                                                </div>
                                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <motion.div 
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${Math.min(100, (slot.guests / (restaurant.reservationSettings?.maxCapacityPerSlot || 1)) * 100)}%` }}
                                                        className={`h-full ${ (slot.guests / (restaurant.reservationSettings?.maxCapacityPerSlot || 1)) > 0.9 ? 'bg-red-600' : 'bg-gray-900'}`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button 
                                onClick={() => setShowTimelineModal(false)}
                                className="w-full mt-8 py-4 bg-gray-100 text-gray-900 rounded-2xl font-black uppercase tracking-widest text-xs"
                            >
                                Fermer
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminReservationsScreen;
